import React, { useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface RayTracingProps {
  width?: number;
  height?: number;
}

export const RayTracing: React.FC<RayTracingProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerY = size / 2;
    const midX = size / 2;

    // Divider
    ctx.beginPath();
    ctx.moveTo(midX, 0);
    ctx.lineTo(midX, size);
    ctx.strokeStyle = '#1E293B';
    ctx.lineWidth = 2;
    ctx.stroke();

    const time = frameCount * 0.05;
    const pulse = Math.sin(time) * 3 + 3; // 0 to 6

    // --- LEFT SIDE: Refractor (Convex Lens) ---
    const l_startX = 10;
    const l_lensX = midX - 60;
    const l_focalX = midX - 10;
    
    // Draw Convex Lens
    ctx.beginPath();
    ctx.moveTo(l_lensX, centerY - 50);
    ctx.quadraticCurveTo(l_lensX + 20, centerY, l_lensX, centerY + 50);
    ctx.quadraticCurveTo(l_lensX - 20, centerY, l_lensX, centerY - 50);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.stroke();

    // Draw Rays
    ctx.strokeStyle = '#FDE047';
    ctx.lineWidth = 1.5;
    const rayOffsets = [-30, -15, 0, 15, 30];
    
    rayOffsets.forEach(offset => {
      // Incoming
      ctx.beginPath();
      ctx.moveTo(l_startX, centerY + offset);
      ctx.lineTo(l_lensX, centerY + offset);
      ctx.stroke();

      // Bending to focal point
      ctx.beginPath();
      ctx.moveTo(l_lensX, centerY + offset);
      ctx.lineTo(l_focalX, centerY);
      // And continuing a bit
      ctx.lineTo(l_focalX + 15, centerY - (offset * 0.5));
      ctx.stroke();
    });

    // Pulse focal point
    ctx.beginPath();
    ctx.arc(l_focalX, centerY, pulse, 0, Math.PI*2);
    ctx.fillStyle = 'rgba(253, 224, 71, 0.8)';
    ctx.fill();

    // Labels
    ctx.fillStyle = '#94A3B8';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Glass Lens — bends light', midX / 2, size - 20);
    ctx.fillStyle = '#E2E8F0';
    ctx.font = 'bold 14px sans-serif';
    ctx.fillText('REFRACTOR', midX / 2, 30);


    // --- RIGHT SIDE: Reflector (Concave Mirror) ---
    const r_startX = midX + 10;
    const r_mirrorX = size - 20;
    const r_focalX = size - 70;

    // Draw Concave Mirror
    ctx.beginPath();
    ctx.moveTo(r_mirrorX - 10, centerY - 50);
    ctx.quadraticCurveTo(r_mirrorX + 20, centerY, r_mirrorX - 10, centerY + 50);
    ctx.strokeStyle = '#94A3B8';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Draw Rays
    ctx.strokeStyle = '#FDE047';
    ctx.lineWidth = 1.5;
    
    rayOffsets.forEach(offset => {
      // Incoming
      ctx.beginPath();
      ctx.moveTo(r_startX, centerY + offset);
      const hitX = r_mirrorX - 10 + (Math.abs(offset) / 30) * 15; // approximate curve hit
      ctx.lineTo(hitX, centerY + offset);
      ctx.stroke();

      // Bouncing to focal point
      ctx.beginPath();
      ctx.moveTo(hitX, centerY + offset);
      ctx.lineTo(r_focalX, centerY);
      // Continuing a bit
      ctx.lineTo(r_focalX - 15, centerY - (offset * 0.3));
      ctx.stroke();
    });

    // Pulse focal point
    ctx.beginPath();
    ctx.arc(r_focalX, centerY, pulse, 0, Math.PI*2);
    ctx.fillStyle = 'rgba(253, 224, 71, 0.8)';
    ctx.fill();

    // Labels
    ctx.fillStyle = '#94A3B8';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Curved Mirror — bounces light', midX + midX / 2, size - 20);
    ctx.fillStyle = '#E2E8F0';
    ctx.font = 'bold 14px sans-serif';
    ctx.fillText('REFLECTOR', midX + midX / 2, 30);

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};
